<template>
  <div>
    <div class="section-hero-subpage bg-color-octrary">
      <video autoplay loop muted playsinline width="640" preload="metadata" poster="/frontend/assets/images/og-image.jpg">
        <source src="/frontend/assets/videos/video.mp4" type="video/mp4" />
        Your browser does not support the video tag.
      </video>
      <div class="container">
        <div class="paragraph--normal text-uppercase text-color-quinary mb-2">
          <span class="notranslate" translate="no">SELECTED</span> CARS
        </div>
        <h1 class="heading--0 text-uppercase text-color-quinary border-bottom-quinary padding-bottom-2 margin-bottom-3-lg">
          Premium <span class="fw-light">European</span><br />
          Brands
        </h1>
        <div class="row">
          <div class="col-lg-7">
            <p class="paragraph--xlarge text-color-senary">
              We source and export the finest vehicles from Europe's most
              prestigious manufacturers. Browse our brand portfolio below.
            </p>
          </div>
        </div>
      </div>
    </div>

    <div class="section-small bg-color-primary">
      <div class="container">
        <h2 class="heading--4 fw-light text-color-quinary text-uppercase border-bottom-quinary padding-bottom-3-lg padding-bottom-2 margin-bottom-5-lg margin-bottom-3">
          Our Brand Portfolio
        </h2>

        <div v-if="loading" class="text-center py-5">
          <div class="spinner-border text-light" role="status">
            <span class="visually-hidden">Loading...</span>
          </div>
        </div>

        <div v-else class="row">
          <div
            v-for="brand in brandsWithCount"
            :key="brand.id"
            class="col-lg-3 col-md-4 col-6 mb-4"
          >
            <router-link :to="`/brands/${encodeURIComponent(brand.name.toLowerCase())}`" class="brand-card">
              <div class="brand-card--logo">
                <img
                  v-if="brand.logo_path"
                  :src="'/storage/' + brand.logo_path"
                  :alt="brand.name + ' logo'"
                />
                <div v-else class="brand-card--placeholder">
                  {{ brand.name.charAt(0) }}
                </div>
              </div>
              <div class="brand-card--name">{{ brand.name }}</div>
              <div class="brand-card--count">{{ brand.vehicleCount }} {{ brand.vehicleCount === 1 ? 'car' : 'cars' }} available</div>
            </router-link>
          </div>
        </div>
      </div>
    </div>

    <AppMarquee />
  </div>
</template>

<script>
import AppMarquee from '../components/AppMarquee.vue';
import axios from 'axios';

export default {
  components: { AppMarquee },
  data() {
    return {
      brands: [],
      vehicles: [],
      loading: true
    };
  },
  computed: {
    brandsWithCount() {
      return this.brands
        .map(b => ({
          ...b,
          vehicleCount: this.vehicles.filter(
            v => v.make && v.make.toLowerCase() === b.name.toLowerCase() && v.status === 'active'
          ).length
        }))
        .sort((a, b) => b.vehicleCount - a.vehicleCount);
    }
  },
  async mounted() {
    import('../seo.js').then(({ setPageSeo }) => setPageSeo({
      title: 'Premium Car Brands for Export | BMW, Mercedes, Porsche, Ferrari | Selected Cars',
      description: 'Browse premium European car brands available for worldwide export. BMW, Mercedes-Benz, Porsche, Audi, Ferrari, Lamborghini, Land Rover and more. Source luxury vehicles direct from Europe with door-to-door delivery to UAE, Saudi Arabia, Qatar, Africa, Asia.',
      keywords: 'european car brands for export, BMW export europe, Mercedes-Benz export, Porsche buy from europe, Ferrari export worldwide, Lamborghini from europe, Audi export, Land Rover europe export, luxury brands for sale europe, premium car brands international delivery',
      breadcrumbs: [
        { name: 'Home', url: '/' },
        { name: 'Brands', url: '/brands' }
      ]
    }));

    try {
      const [brandsRes, vehiclesRes] = await Promise.all([
        axios.get('/api/brands'),
        axios.get('/api/vehicles')
      ]);
      this.brands = brandsRes.data;
      const raw = Array.isArray(vehiclesRes.data) ? vehiclesRes.data : (vehiclesRes.data.data || []);
      this.vehicles = raw;
    } catch (e) {
      console.error('Error loading brands:', e);
    } finally {
      this.loading = false;
    }
  }
};
</script>

<style scoped>
.brand-card {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding: 2rem 1rem;
  background: #222;
  border: 1px solid rgba(255,255,255,0.18);
  text-decoration: none;
  transition: border-color 0.3s, background-color 0.3s;
  height: 100%;
}
.brand-card:hover {
  background: #2a2a2a;
  border-color: rgba(255,255,255,0.35);
}
.brand-card--logo {
  width: 80px;
  height: 80px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 1rem;
}
.brand-card--logo img {
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
  filter: brightness(0) invert(1);
}
.brand-card--placeholder {
  width: 80px;
  height: 80px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 2.5rem;
  font-weight: 700;
  color: rgba(255,255,255,0.45);
  border: 1px solid rgba(255,255,255,0.15);
}
.brand-card--name {
  font-size: 1.1rem;
  font-weight: 600;
  color: #fff;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  margin-bottom: 0.3rem;
}
.brand-card--count {
  font-size: 0.75rem;
  color: rgba(255,255,255,0.55);
  letter-spacing: 0.05em;
}
</style>
